import { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/admin';

/**
 * Hook pour vérifier si l'utilisateur a un rôle admin
 *
 * SÉCURITÉ : Ce hook vérifie le rôle côté client, mais la vraie sécurité
 * doit être assurée par les Firestore Security Rules côté serveur.
 *
 * Usage:
 * ```tsx
 * const { isAdmin, role, loading } = useAdminRole();
 *
 * if (loading) return <Skeleton />;
 * if (!isAdmin) return <Navigate to="/dashboard" />;
 *
 * return <AdminDashboard />;
 * ```
 */
export const useAdminRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>('user');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      setRole('user');
      setLoading(false);
      lastUserIdRef.current = null;
      return;
    }

    // Éviter de refetch si c'est le même utilisateur
    if (lastUserIdRef.current === user.uid) {
      return;
    }

    const checkAdminRole = async () => {
      try {
        setLoading(true);
        // Récupérer le document utilisateur depuis Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userRole = userData.role as UserRole;
          setRole(userRole || 'user');
        } else {
          // Si le document n'existe pas, créer un rôle par défaut
          setRole('user');
        }

        lastUserIdRef.current = user.uid;
        setLoading(false);
      } catch (err) {
        // Error('Error checking admin role:', err);
        setError(err as Error);
        setRole('user'); // Fallback to user role on error
        setLoading(false);
      }
    };

    checkAdminRole();
  }, [user]);

  const isAdmin = role === 'admin';
  const isSupport = role === 'support';
  const isFinance = role === 'finance';
  const isTech = role === 'tech';
  const isCoach = role === 'coach';
  const isORPCounselor = role === 'orp_counselor';

  // Admin, support, finance, and tech roles all have admin panel access
  // Coach and ORP counselor roles do not have admin panel access
  const hasAdminAccess = isAdmin || isSupport || isFinance || isTech;

  return {
    role,
    isAdmin,
    isSupport,
    isFinance,
    isTech,
    isCoach,
    isORPCounselor,
    hasAdminAccess,
    loading,
    error,
  };
};

/**
 * Hook pour vérifier si l'utilisateur peut effectuer une action spécifique
 *
 * Usage:
 * ```tsx
 * const { canPerform } = useAdminPermissions();
 *
 * if (canPerform('suspend_user')) {
 *   return <Button onClick={suspendUser}>Suspendre</Button>;
 * }
 * ```
 */
export const useAdminPermissions = () => {
  const { role } = useAdminRole();

  const permissions: Record<UserRole, string[]> = {
    user: [],
    support: [
      'view_users',
      'view_subscriptions',
      'create_support_ticket',
      'view_analytics',
    ],
    finance: [
      'view_users',
      'view_subscriptions',
      'view_analytics',
      'view_financial_dashboard',
      'issue_refund',
      'modify_subscription',
    ],
    tech: [
      'view_users',
      'view_system_health',
      'view_errors',
      'manage_feature_flags',
      'view_analytics',
      'view_performance',
    ],
    coach: [
      'view_users',
      'view_coaching_sessions',
      'manage_coaching_sessions',
    ],
    orp_counselor: [
      'view_assigned_job_seekers',
      'view_cv_scores',
      'view_applications',
      'view_orp_alerts',
    ],
    admin: [
      // Admin has all permissions
      'view_users',
      'edit_user',
      'suspend_user',
      'delete_user',
      'impersonate_user',
      'view_subscriptions',
      'modify_subscription',
      'issue_refund',
      'create_promo_code',
      'view_system_health',
      'view_errors',
      'resolve_error',
      'view_performance',
      'view_analytics',
      'view_financial_dashboard',
      'manage_feature_flags',
      'manage_admin_access',
      'view_audit_log',
    ],
  };

  const canPerform = (permission: string): boolean => {
    return permissions[role]?.includes(permission) || false;
  };

  return {
    canPerform,
    permissions: permissions[role] || [],
  };
};

export default useAdminRole;
